import { useAccountStore, useAssetStore } from '../../../../store'
import { useShallow } from 'zustand/react/shallow'
import { GoogleAdsAdAccountDTO } from 'api-models'
import { AppButton } from '@clickguard/design-system'

// DEV-2353 — which MCC banner this notice renders, derived from the lifecycle state
// (Field A) + dismissal (Field B) in buildMccLinkRequestNotice.
export type MccBannerType = 'INITIAL' | 'EXPIRED' | 'ACCESS_LOST'

export const MccLinkRequestNotice = ({
	mccLinkInviteAdAccounts,
	bannerType,
	onDismiss,
	onSent
}: {
	mccLinkInviteAdAccounts: GoogleAdsAdAccountDTO[]
	bannerType: MccBannerType
	onDismiss?: () => void
	onSent?: () => void
}) => {
	const account = useAccountStore((a) => a.account)
	const { sendMccLinkRequest } = useAssetStore(
		useShallow((s) => ({
			sendMccLinkRequest: s.sendMccLinkRequest
		}))
	)

	const isMulti = mccLinkInviteAdAccounts.length > 1

	// Sends (or resends) the link request for every account in this banner's group, then
	// opens the one-time accept-instructions modal. Works for the initial invite as well as
	// the expired / access-lost resend, since the send-invite endpoint accepts those states.
	const handleSend = async () => {
		if (!account) return
		for (const adAccount of mccLinkInviteAdAccounts) {
			await sendMccLinkRequest(account.id, adAccount.id)
		}
		onSent?.()
	}

	if (bannerType === 'INITIAL') {
		return (
			<div className="flex items-center justify-between w-full gap-4">
				<p className="text-xl font-medium text-t-base-blue leading-[30px]">
					To unlock <span className="text-t-email-blue">advanced support</span> from
					ClickGuard, you can connect your Google Ads{' '}
					{isMulti ? 'accounts' : 'account'} to our Manager Account (MCC).
				</p>
				<div className="flex items-center gap-4 shrink-0">
					<button
						type="button"
						onClick={onDismiss}
						className="text-base font-medium text-t-base-blue hover:underline"
					>
						Dismiss
					</button>
					<AppButton
						size="sm"
						label={isMulti ? 'Send invitations' : 'Send invitation'}
						onClick={handleSend}
					/>
				</div>
			</div>
		)
	}

	if (bannerType === 'EXPIRED') {
		return (
			<div className="flex items-center justify-between w-full gap-4">
				<div className="flex flex-col gap-1">
					<p className="text-xl font-medium text-t-base-blue leading-[30px]">
						Our MCC request expired
					</p>
					<p className="text-base font-medium text-t-base-blue">
						Please send the invitation again, then go to{' '}
						<span className="font-semibold">Google Ads</span> →{' '}
						<span className="font-semibold">Admin</span> →{' '}
						<span className="font-semibold">Access and security</span> →{' '}
						<span className="font-semibold">Managers</span> and click{' '}
						<span className="font-semibold">Accept</span>.
					</p>
				</div>
				<div className="flex items-center gap-4 shrink-0">
					<button
						type="button"
						onClick={onDismiss}
						className="text-base font-medium text-t-base-blue hover:underline"
					>
						Dismiss
					</button>
					<AppButton
						size="sm"
						label="Resend invitation"
						onClick={handleSend}
					/>
				</div>
			</div>
		)
	}

	if (bannerType === 'ACCESS_LOST') {
		return (
			<div className="flex items-center justify-between w-full gap-4">
				<div className="flex flex-col gap-1">
					<p className="text-xl font-medium text-t-base-blue leading-[30px]">
						Your Google Ads {isMulti ? 'accounts are' : 'account is'} no longer
						linked to our MCC.
					</p>
					<p className="text-base font-medium text-t-base-blue">
						Reconnect to resume automatic refunds
					</p>
				</div>
				<div className="flex items-center gap-4 shrink-0">
					<AppButton
						size="sm"
						label="Resend invitation"
						onClick={handleSend}
					/>
				</div>
			</div>
		)
	}

	return <></>
}
